import { EmptyState } from '@components/EmptyState';
import { useBankProfile } from '@contexts/BankProfileContext';
import {
	Button,
	type ColumnProps,
	DropdownPositions,
	MessagesIcon,
	Pagination,
	PlusIcon,
	Table,
} from '@gurupay/gp-ui-library';
import { type IMessage, useMessages } from '@hooks/useMessages';
import { usePagination } from '@hooks/usePagination';
import clsx from 'clsx';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { MessageItemCell } from './MessageItemCell';
import { NonBreakingDate } from './NonBreakingDate';

const getRenderers = (selectedProfileName: string, t: TFunction) => ({
	topic: ({ topic, unread_count }: IMessage) => (
		<span className={unread_count ? 'text-sm-bold' : ''}>
			{t(`topic.${topic.value}`)}
		</span>
	),
	author: ({ author, unread_count }: IMessage) => (
		<span className={clsx('text-neutral-800', unread_count && 'text-sm-bold')}>
			{author || selectedProfileName}
		</span>
	),
	updatedAt: ({ updated_at, unread_count }: IMessage) => (
		<span className={clsx('text-neutral-800 pl-space-8', unread_count && 'text-sm-bold')}>
			<NonBreakingDate date={updated_at} />
		</span>
	),
	thread: ({ topic, author, unread_count }: IMessage) => (
		<MessageItemCell
			author={author || selectedProfileName}
			isUnread={!!unread_count}
			topic={topic.name}
		/>
	),
});

const Messages = () => {
	const { t } = useTranslation('', { keyPrefix: 'messages' });
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();
	const { selectedProfile } = useBankProfile();
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const renderers = getRenderers(selectedProfile?.name, t);
	const columns: ColumnProps[] = isMobile
		? [
				{ label: '', key: 'thread', align: 'left' },
				{ label: t('list.updatedAt'), key: 'updatedAt', minWidth: '90px' },
			]
		: [
				{ label: t('list.subject'), key: 'topic', align: 'left' },
				{ label: t('list.author'), key: 'author', align: 'left' },
				{ label: t('list.updatedAt'), key: 'updatedAt' },
			];

	const navigate = useNavigate();

	const handleRowClick = ({ uuid }: IMessage) => {
		navigate(`/messages/${uuid}`);
	};

	const { data, isLoading } = useMessages(currentPage, pageSize);

	const getRowClassname = ({ unread_count }: IMessage) =>
		unread_count ? 'bg-brand-secondary-50 hover:bg-brand-secondary-100' : '';
	return (
		<div>
			{!data?.items.length && !isLoading ? (
				<EmptyState
					buttonProps={{
						children: t('empty.buttonText'),
						href: '/messages/new',
					}}
					icon={<MessagesIcon />}
					subTitle={t('empty.subTitle')}
					title={t('empty.title')}
				/>
			) : (
				<>
					<Button
						href="/messages/new"
						className="flex-row-reverse inline-flex"
						icon={<PlusIcon />}
						variant="primary-black"
					>
						{t('newMessage.button')}
					</Button>
					<Table
						className="mt-space-40 max-mobile:mt-space-24 table-fixed"
						columns={columns}
						getRowClassName={getRowClassname}
						isLoading={isLoading}
						onRowClick={handleRowClick}
						renders={renderers}
						rows={data?.items ?? []}
						showHeader={!isMobile}
					/>
				</>
			)}
			{data?.pagination && data.pagination.count > pageSize && (
				<Pagination
					className="mt-space-32!"
					currentPage={currentPage}
					dropdownOpenPosition={DropdownPositions.TOP}
					onPageChange={setCurrentPage}
					onPageSizeChange={handlePageSizeChange}
					pageOptions={pageOptions}
					pageSize={pageSize}
					pageText={pageText}
					totalCount={data.pagination.count}
					totalCountText={totalCountText}
				/>
			)}
		</div>
	);
};

export default Messages;
